/// <amd-module name="Build/Extensions/GitHub/GitHubWizard" />
import * as React from "react";

import * as Build from "Build/Client/Contracts/Build";
import { IInstallAppService } from "Build/Common/Library/InstallAppService";
import * as Resources from "Build/Designer/Resources";
import * as VisibilityMismatch from "Build/Designer/VisibilityMismatchWarning";
import { clearWarning, showWarning } from "Build/Designer/WarningBanner";
import { StepCompletedCallback, WizardState } from "Build/Designer/Wizard.types";
import { ServiceConnectionData, ServiceConnectionInfo } from "Build/Extensions/Connection-Services/ServiceConnectionRetrieverService";
import * as GitHubResources from "Build/Extensions/GitHub/Resources";

// pages
import { ConnectionSelection } from "Build/Extensions/GitHub/ConnectionSelection";
import { RepositorySelection } from "Build/Extensions/GitHub/RepositorySelection";
import { Constant,
         IServiceConnectionCreationService,
         UserAuthorization } from "Build/Extensions/Connection-Services/ServiceConnectionCreationService";
import { RepositoryTypes } from "Build/SourceProviders/Common";

import { ITfsPageService } from "Tfs/Platform/Page";

import { localeComparer } from "VSS/Core/Util/String";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { IVssHistoryService, StateMergeOptions } from "VSS/Platform/History";
import { Components, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";

export enum Steps {
    Loading = 1,
    SelectConnection = 2,
    SelectRepository = 3
}

namespace WizardStateKeys {
    export const ConnectionId = "connectionId";
    export const RepositoryId = "repositoryId";
    export const ProjectId = "projectId";
}

export interface ServiceConnectionRecommendation {
    commonConnectionId: string;
    userConnectionId: string;
    appInstalled: boolean;
    appInstalledForRepository: boolean;
    installAppUrlRoot: string;
    userCanInstall: boolean;
    repositoryMappedToAnotherCollection: boolean;
}

export interface Props {
    wizardState: WizardState;
    onComplete: StepCompletedCallback;
    onUpdate: (action: string, properties?: { [key: string]: any }) => void;
    onTTI: () => void;
}

export interface State {
    stepNumber: number;
    connectionId?: string;
    repositoryId?: string;
    loadingText?: string;
    errorMessage?: string;
}

export class GitHubReposWizard extends VssComponent<Props, State, IVssComponentContext> {
    private static readonly _notUsingMarketplaceWarningBanner: string = "_warnIfNotUsingMarketplaceApp";

    private _projectId: string;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const pageService = this.context.pageContext.getService<ITfsPageService>("ITfsPageService");
        const pageData = pageService.getData();
        this._projectId = pageData!.project!.id;

        this.state = this._getState(props);

        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.github-serviceconnections-data-provider";
        contributionService.getDataAsync<ServiceConnectionData>(dataProviderId, { "endpointType": this.props.wizardState.sourceProvider }, true)
            .then((serviceConnectionData) => {
                let serviceConnections: ServiceConnectionInfo[] = [];
                if (serviceConnectionData) {
                    serviceConnections = serviceConnectionData.serviceConnections || [];
                    serviceConnections.sort((a, b) => localeComparer(a.name, b.name));
                }

                const hasNewConnection = !!this.props.wizardState.key;
                if (hasNewConnection) {
                    // We don't show the connection selection unless a user explicitly goes there,
                    // so don't do any automatic connection management work on that page
                    if (this.state.stepNumber !== Steps.SelectConnection && !this.props.wizardState.showSelectConnection) {
                        // After automatic OAuth show the repository selection
                        this.setState({
                            stepNumber: Steps.SelectRepository
                        });
                    } else if (this.state.stepNumber === Steps.SelectConnection && this.props.wizardState.showSelectConnection) {
                        // This is when the 'Authorize' button is pressed in the ConnectionSelection:
                        //  - on the 'select connection' screen
                        //  - have a 'key', so we were redirected from OAuth

                        // Save the connection and use it
                        const userAuthorization = {
                            scheme: Constant.OAuthScheme,
                            strongboxKey: this.props.wizardState.key,
                            displayName: this.props.wizardState.githubhandle,
                            avatarUrl: this.props.wizardState.avatarurl
                        } as UserAuthorization;

                        const service = this.context.pageContext.getService<IServiceConnectionCreationService>("IServiceConnectionCreationService");
                        service.createOrUpdateEndpointForUserAuth(context, this.props.wizardState.sourceProvider, this._projectId, serviceConnections, userAuthorization,
                            x => x.displayName === this.props.wizardState.githubhandle && x.scheme === Constant.OAuthScheme)
                            .then((serviceEndpoint) => this._onSelectConnection(serviceEndpoint.id));
                    }
                } else if (!this.props.wizardState.connectionId && !this.props.wizardState.showSelectConnection) {
                    // Automatically OAuth
                    const service = this.context.pageContext.getService<IServiceConnectionCreationService>("IServiceConnectionCreationService");
                    service.createAuthRequest(context, this.props.wizardState.sourceProvider, this._projectId, true, this._onAuthError);
                } else {
                    // The user has manually selected a connection
                    const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                    if (!!exception) {
                        this.setState({ errorMessage: exception && exception.message });
                    }
                }
            }).catch((error: Error) => {
                this.setState({ errorMessage: error && error.message });
            });
    }

    public render(): JSX.Element {
        return (<>
        {
            this.state.errorMessage && <MessageCard className={"github-wizard-error"} severity={MessageCardSeverity.Error}>
                {this.state.errorMessage}
            </MessageCard>
        }
        {
            this._renderStep()
        }
        </>);
    }

    public componentWillReceiveProps(newProps: Props) {
        this.setState(this._getState(newProps));
    }

    public componentDidUpdate(prevProps: Props, prevState: State) {
        // When moving back from selecting a repository to selecting a connection, remove any error messages
        if (prevState && prevState.stepNumber == Steps.SelectRepository && this.state.stepNumber == Steps.SelectConnection) {
            this._clearErrorMessage();
        }
    }

    public componentDidMount() {
        // clear when the user presses 'back' and there is a banner displayed
        clearWarning(this.context.pageContext, GitHubReposWizard._notUsingMarketplaceWarningBanner);
        clearWarning(this.context.pageContext, VisibilityMismatch.bannerId);
    }

    private _renderStep(): JSX.Element {
        switch (this.state.stepNumber) {
            case Steps.Loading:
                return <Spinner size={SpinnerSize.large} label={this.state.loadingText}/>;
            default:
            case Steps.SelectConnection:
                return <ConnectionSelection onSelectConnection={this._onSelectConnection}
                    serviceName={GitHubResources.GitHub}
                    sourceProvider={this.props.wizardState.sourceProvider}
                    onErrorMessage={this._onErrorMessage}/>;
            case Steps.SelectRepository:
                return <RepositorySelection onSelectRepository={this._onSelectRepository}
                    connectionId={this.state.connectionId!}
                    serviceName={GitHubResources.GitHub}
                    sourceProvider={this.props.wizardState.sourceProvider}
                    strongBoxKey={this.props.wizardState.key}
                    autoSelectRepositoryId={this.props.wizardState.repositoryId}
                    onRepositoryListUpdate={this._onRepositoryListUpdate}
                    onErrorMessage={this._onErrorMessage}
                    onTTI={this.props.onTTI}/>;
        }
    }

    private _onErrorMessage = (error: string): void => {
        this.setState({ errorMessage: error });
    }

    private _onSelectConnection = (connectionId: string): void => {
        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");

        const currentState = historyService.getState();
        // remove everything related with redirecting the page
        const redirectedBack = !!(currentState['key']);
        if (redirectedBack) {
            this._removeProperty(currentState, "avatarurl");
            this._removeProperty(currentState, "key");
            this._removeProperty(currentState, "githubhandle");
            this._removeProperty(currentState, "showSelectConnection");
        }

        const url: string = historyService.generateUrl({
            ...currentState,
            connectionId: connectionId
        }, StateMergeOptions.none);

        if (redirectedBack) {
            // we should hide the redirect url by replacing it; otherwise we end up with
            // stale key values in url (the key has already being deleted once we created
            // service endpoint)
            historyService.replaceState(undefined, url);
        } else {
            historyService.pushState(undefined, url);
        }

        this.setState({
            stepNumber: Steps.SelectRepository,
            connectionId: connectionId
        });
    }

    private _removeProperty(obj: any, key: string) {
        if (obj[key]) {
            delete obj[key]
        }
    }

    private _onSelectRepository = (repository: Build.SourceRepository, properties: { [key: string]: string }): void => {
        if (VisibilityMismatch.isVisibilityMismatch(this.context.pageContext, repository)) {
            var warningBanner = VisibilityMismatch.getBannerContent(this.context.pageContext, repository);
            showWarning(this.context.pageContext, warningBanner);
        }

        if (!this.state.connectionId) {
            this._completeWithBestConnection(repository, properties);
        } else {
            this.props.onComplete({
                repositoryId: repository.id,
                repositoryName: repository.id,
                branch: repository.defaultBranch,
                connectionId: this.state.connectionId!,
                repositoryUrl: properties.repositoryUrl
            }, properties);
        }
    }

    private _completeWithBestConnection(repository: Build.SourceRepository, properties: { [key: string]: string }) {
        this.setState({ stepNumber: Steps.Loading, loadingText: GitHubResources.ValidatingPermissions }); // show a progress spinner when doing expensive data-provider calls

        // Attempt to find a better service endpoint (i.e. see if we have the Marketplace App installed for this org)
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.app-serviceconnections-recommendation-data-provider";
        contributionService.getDataAsync<ServiceConnectionRecommendation>(dataProviderId,
            {
                sourceProvider: this.props.wizardState.sourceProvider,
                repositoryId: repository.id,
                repositoryName: repository.name,
                connectionId: this.state.connectionId,
                createOAuthConnectionIfNeeded: true,
                strongBoxKey: this.props.wizardState.key,
                avatarUrl: this.props.wizardState.avatarurl,
                login: this.props.wizardState.githubhandle
            },
            true)
        .then((serviceConnectionRecommendation) => {
            let newConnectionId = this.state.connectionId;
            if (serviceConnectionRecommendation) {
                // If we got a connection id back use that instead of the current connection
                newConnectionId = serviceConnectionRecommendation.commonConnectionId || serviceConnectionRecommendation.userConnectionId;
                if (newConnectionId) {
                    this.setState({
                        connectionId: newConnectionId
                    });
                }

                // If the app isn't installed, and the user can install the app, redirect them to do so
                if (!serviceConnectionRecommendation.appInstalledForRepository && serviceConnectionRecommendation.userCanInstall) {
                    // Redirect the user to install the app for this repository
                    const installAppService = this.context.pageContext.getService<IInstallAppService>("IInstallAppService");
                    installAppService.installAppForWizard(RepositoryTypes.GitHub, newConnectionId, serviceConnectionRecommendation.installAppUrlRoot, repository);
                    return;
                }

                // Finally, add or remove the marketplace app warning if needed
                this._warnIfNotUsingMarketplaceApp(serviceConnectionRecommendation, repository);

                // Now complete the repository selection
                this.props.onComplete({
                    repositoryId: repository.id,
                    repositoryName: repository.id,
                    branch: repository.defaultBranch,
                    connectionId: newConnectionId,
                    repositoryUrl: properties.repositoryUrl
                }, properties);
            } else {
                const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                this.setState({
                    errorMessage: exception && exception.message,
                    stepNumber: Steps.SelectRepository // 'loading' doesn't make sense, we had repositories, so maybe another will work?
                });
            }
        }).catch((error: Error) => {
            this.setState({ errorMessage: error && error.message });
        });
    }

    private _warnIfNotUsingMarketplaceApp(recommendation: ServiceConnectionRecommendation, repository: Build.SourceRepository) {
        if (recommendation.repositoryMappedToAnotherCollection) {
            // Warn the user that the repo is mapped to another org (they can still continue, but they will use OAuth)
            const appInstallUrl = "https://go.microsoft.com/fwlink/?linkid=874367";
            showWarning(
                this.context.pageContext,
                {
                    id: GitHubReposWizard._notUsingMarketplaceWarningBanner,
                    messageFormat: Resources.RepositoryAlreadyMapped,
                    links: [
                        { name: repository.fullName, href: repository.url },
                        { name: Resources.AzurePipelinesApp, href: appInstallUrl },
                        { name: Resources.LearnMore, href: "https://go.microsoft.com/fwlink/?linkid=2053028#create-pipelines-in-multiple-azure-devops-organizations-and-projects" }
                    ]
                });
        }
        else if (!recommendation.appInstalled && !recommendation.userCanInstall) {
            // Warn the user that the app isn't installed for this repo (they can still continue, but they will use OAuth)
            const appInstallUrl = "https://go.microsoft.com/fwlink/?linkid=874367";
            showWarning(
                this.context.pageContext,
                {
                    id: GitHubReposWizard._notUsingMarketplaceWarningBanner,
                    messageFormat: Resources.UnableToInstallMarketplaceApp,
                    links: [
                        { name: Resources.AzurePipelinesApp, href: appInstallUrl },
                        { name: repository.fullName, href: repository.url },
                        { name: Resources.LearnMore, href: "https://go.microsoft.com/fwlink/?linkid=2053028#integrate-using-the-github-app" }
                    ]
                });
        }
    }

    private _getState(props: Props): State {
        let stepNumber: number = Steps.SelectConnection;
        const connectionId: string = props.wizardState[WizardStateKeys.ConnectionId];
        const repositoryId: string = props.wizardState[WizardStateKeys.RepositoryId];

        if (connectionId && !repositoryId) {
            stepNumber = Steps.SelectRepository;
        } else if (connectionId && repositoryId) {
            // The URL has the repo id of the repo to use. This means we are returning from installing the app
            stepNumber = Steps.SelectRepository
        } else if (!props.wizardState.showSelectConnection) {
            // We are in the process of OAuthing with GitHub
            stepNumber = Steps.Loading;
        }

        return {
            stepNumber: stepNumber,
            connectionId: connectionId,
            repositoryId: repositoryId
        };
    }

    private _onRepositoryListUpdate = (action: string, properties?: { [key: string]: any }): void => {
        this.props.onUpdate(action, properties);
    }

    private _onAuthError = (e: any): void => {
        // on error (which should happen only when in the 'LoadingOAuth' step) show the connection page
        // this should allow the user to see any authorization errors
        this.setState({
            stepNumber: Steps.SelectConnection,
            errorMessage: e
        });
    }

    private _clearErrorMessage = () => {
        this.setState({
            errorMessage: undefined
        });
    }
}

Components.add("github-wizard", GitHubReposWizard);
